import React from 'react';
import { END_OF_LIST } from './constants';
import { TaxEngine } from './services';
import { fmt } from './utils';
import { useStore } from '../../store/useStore';

interface VoucherGridProps {
  voucherType: string;
  derivedVoucherConfig: Record<string, any>;
  derivedJournalTypes: Set<string>;
  formData: any;
  focusedField: string;
  focusField: (id: string) => void;
  handleInputChange: (fieldId: string, value: string) => void;
  handleKeyDown: (e: React.KeyboardEvent, fieldId: string) => void;
  inputRefs: React.MutableRefObject<Record<string, any>>;
  ledgerDb: Record<string, any>;
  baseCurrency?: any;
  drTotal: number;
  crTotal: number;
}

export const VoucherGrid: React.FC<VoucherGridProps> = ({
  voucherType, derivedVoucherConfig, derivedJournalTypes, formData, focusedField,
  focusField, handleInputChange, handleKeyDown, inputRefs, ledgerDb, baseCurrency,
  drTotal, crTotal,
}) => {
  const cfg = derivedVoucherConfig[voucherType];
  const grandTotal = TaxEngine.calculateFinalAmount(formData.items, formData.charges);
  const activeCompany = useStore((s) => s.activeCompany);
  const isPharmacy = (activeCompany as any)?.businessNature === 'pharmacy';

  return (
    <>
      {/* ======================= */}
      {/* SCROLLABLE BODY         */}
      {/* ======================= */}
      <div className="flex-1 overflow-y-auto bg-white flex flex-col min-h-0 relative">
        {cfg?.useJournalGrid && (
          <div
            className="flex-1 flex flex-col h-full bg-white pb-10"
            onClick={(e: any) => {
              if (e.target === e.currentTarget)
                focusField(`journal_${formData.journalRows.length - 1}_ledger`);
            }}
          >
            {formData.journalRows.map((row: any, idx: number) => {
              const lfocused = focusedField === `journal_${idx}_ledger`;
              const cfocused = focusedField === `journal_${idx}_drcr`;
              return (
                <div key={idx} className="flex border-b border-[#f0f0f0] text-[13px] w-full box-border">
                  <div className="w-[50px] min-w-[50px] max-w-[50px] shrink-0 border-r border-[#f0f0f0] box-border">
                    <input
                      ref={(el: any) => { inputRefs.current[`journal_${idx}_drcr`] = el; }}
                      type="text"
                      value={row.drCr}
                      onChange={e => handleInputChange(`journal_${idx}_drcr`, e.target.value)}
                      onFocus={(e: any) => { e.stopPropagation(); focusField(`journal_${idx}_drcr`); }}
                      onKeyDown={e => handleKeyDown(e as any, `journal_${idx}_drcr`)}
                      className={`w-full max-w-full box-border min-w-0 outline-none px-1 py-[3px] text-center font-bold ${cfocused ? 'bg-[#eefcfc]' : 'bg-transparent text-[#1e8585]'}`}
                      maxLength={2} spellCheck={false}
                    />
                  </div>
                  <div className="flex-1 min-w-0 border-r border-[#f0f0f0] flex flex-col justify-center box-border">
                    <input
                      ref={(el: any) => { inputRefs.current[`journal_${idx}_ledger`] = el; }}
                      type="text"
                      value={lfocused ? row.ledger.text : row.ledger.value}
                      onChange={e => handleInputChange(`journal_${idx}_ledger`, e.target.value)}
                      onFocus={(e: any) => { e.stopPropagation(); focusField(`journal_${idx}_ledger`); }}
                      onKeyDown={e => handleKeyDown(e as any, `journal_${idx}_ledger`)}
                      className={`w-full max-w-full box-border min-w-0 outline-none px-2 py-[3px] border-transparent font-medium ${lfocused ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                      spellCheck={false}
                    />
                    {row.ledger.value && ledgerDb[row.ledger.value] && !lfocused && row.ledger.value !== END_OF_LIST && (
                      <div className="text-[10px] italic text-[#6a7f95] px-2 pb-[2px] mt-[-2px] box-border truncate">
                        Cur Bal: {ledgerDb[row.ledger.value].balance}
                      </div>
                    )}
                  </div>
                  <div className="w-[130px] min-w-[130px] max-w-[130px] shrink-0 border-r border-[#f0f0f0] box-border">
                    <input
                      ref={(el: any) => { inputRefs.current[`journal_${idx}_debit`] = el; }}
                      type="text"
                      value={row.debit}
                      disabled={row.drCr !== 'Dr'}
                      onChange={e => handleInputChange(`journal_${idx}_debit`, e.target.value)}
                      onFocus={(e: any) => { e.stopPropagation(); focusField(`journal_${idx}_debit`); }}
                      onKeyDown={e => handleKeyDown(e as any, `journal_${idx}_debit`)}
                      className={`w-full max-w-full h-full box-border min-w-0 outline-none px-2 py-[3px] text-right font-semibold ${focusedField === `journal_${idx}_debit` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                    />
                  </div>
                  <div className="w-[130px] min-w-[130px] max-w-[130px] shrink-0 box-border">
                    <input
                      ref={(el: any) => { inputRefs.current[`journal_${idx}_credit`] = el; }}
                      type="text"
                      value={row.credit}
                      disabled={row.drCr !== 'Cr'}
                      onChange={e => handleInputChange(`journal_${idx}_credit`, e.target.value)}
                      onFocus={(e: any) => { e.stopPropagation(); focusField(`journal_${idx}_credit`); }}
                      onKeyDown={e => handleKeyDown(e as any, `journal_${idx}_credit`)}
                      className={`w-full max-w-full h-full box-border min-w-0 outline-none px-2 py-[3px] text-right font-semibold ${focusedField === `journal_${idx}_credit` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {cfg?.useItemGrid && (
          <div
            className="flex-1 flex flex-col h-full bg-white pb-10"
            onClick={(e: any) => {
              if (e.target === e.currentTarget)
                focusField(`item_${formData.items.length - 1}_name`);
            }}
          >
            {/* ITEMS */}
            {formData.items.map((item: any, idx: number) => {
              const active = !!item.name.value;
              return (
                <div key={`item_${idx}`} className="flex border-b border-[#f0f0f0] text-[13px] w-full box-border">
                  <div className="flex-1 min-w-0 border-r border-[#f0f0f0] box-border">
                    <input
                      ref={(el: any) => { inputRefs.current[`item_${idx}_name`] = el; }}
                      type="text"
                      value={focusedField === `item_${idx}_name` ? item.name.text : item.name.value}
                      onChange={e => handleInputChange(`item_${idx}_name`, e.target.value)}
                      onFocus={(e: any) => { e.stopPropagation(); focusField(`item_${idx}_name`); }}
                      onKeyDown={e => handleKeyDown(e as any, `item_${idx}_name`)}
                      className={`w-full max-w-full h-full box-border min-w-0 outline-none px-2 py-[2px] font-medium border-transparent ${focusedField === `item_${idx}_name` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                      spellCheck={false}
                    />
                  </div>
                  {/* Pharmacy: Batch + Expiry columns */}
                  {isPharmacy && active && (
                    <>
                      <div className="w-[100px] min-w-[100px] max-w-[100px] shrink-0 border-r border-[#f0f0f0] box-border">
                        <input
                          ref={(el: any) => { inputRefs.current[`item_${idx}_batch`] = el; }}
                          type="text" placeholder="Batch"
                          value={item.batch || ''}
                          onChange={e => handleInputChange(`item_${idx}_batch`, e.target.value)}
                          onFocus={(e: any) => { e.stopPropagation(); focusField(`item_${idx}_batch`); }}
                          onKeyDown={e => handleKeyDown(e as any, `item_${idx}_batch`)}
                          className={`w-full h-full outline-none px-2 py-[2px] text-[12px] ${focusedField === `item_${idx}_batch` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                        />
                      </div>
                      <div className="w-[80px] min-w-[80px] max-w-[80px] shrink-0 border-r border-[#f0f0f0] box-border">
                        <input
                          ref={(el: any) => { inputRefs.current[`item_${idx}_expiry`] = el; }}
                          type="text" placeholder="MM/YY"
                          value={item.expiry || ''}
                          onChange={e => handleInputChange(`item_${idx}_expiry`, e.target.value)}
                          onFocus={(e: any) => { e.stopPropagation(); focusField(`item_${idx}_expiry`); }}
                          onKeyDown={e => handleKeyDown(e as any, `item_${idx}_expiry`)}
                          className={`w-full h-full outline-none px-2 py-[2px] text-[12px] ${focusedField === `item_${idx}_expiry` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                        />
                      </div>
                    </>
                  )}
                  {(['qty', 'rate', 'disc'] as const).map(col => (
                    <div
                      key={col}
                      className={`${col === 'qty' ? 'w-[80px] min-w-[80px] max-w-[80px]' : col === 'disc' ? 'w-[65px] min-w-[65px] max-w-[65px]' : 'w-[100px] min-w-[100px] max-w-[100px]'} shrink-0 border-r border-[#f0f0f0] box-border ${!active ? 'pointer-events-none' : ''}`}
                    >
                      {active && (
                        <input
                          ref={(el: any) => { inputRefs.current[`item_${idx}_${col}`] = el; }}
                          type="text"
                          placeholder={col === 'disc' ? '%' : ''}
                          value={item[col]}
                          onChange={e => handleInputChange(`item_${idx}_${col}`, e.target.value)}
                          onFocus={(e: any) => { e.stopPropagation(); focusField(`item_${idx}_${col}`); }}
                          onKeyDown={e => handleKeyDown(e as any, `item_${idx}_${col}`)}
                          className={`w-full max-w-full h-full box-border min-w-0 outline-none px-2 py-[2px] text-right ${focusedField === `item_${idx}_${col}` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                        />
                      )}
                    </div>
                  ))}
                  <div className={`w-[160px] min-w-[160px] max-w-[160px] shrink-0 box-border ${!active ? 'pointer-events-none' : ''}`}>
                    {active && (
                      <input
                        ref={(el: any) => { inputRefs.current[`item_${idx}_amount`] = el; }}
                        type="text"
                        value={item.amount}
                        onChange={e => handleInputChange(`item_${idx}_amount`, e.target.value)}
                        onFocus={(e: any) => { e.stopPropagation(); focusField(`item_${idx}_amount`); }}
                        onKeyDown={e => handleKeyDown(e as any, `item_${idx}_amount`)}
                        className={`w-full max-w-full h-full box-border min-w-0 outline-none px-2 py-[2px] text-right font-semibold ${focusedField === `item_${idx}_amount` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {/* TAX & DUTIES */}
            {cfg?.useTaxSection && formData.charges.map((charge: any, idx: number) => {
              if (!charge.ledger.value && !charge.amount && idx !== formData.charges.length - 1) return null;
              const active = !!charge.ledger.value;
              return (
                <div key={`charge_${idx}`} className="flex border-b border-[#f0f0f0] text-[13px] bg-[#fdfefd] w-full box-border">
                  <div className="flex-1 min-w-0 border-r border-[#f0f0f0] box-border">
                    <input
                      ref={(el: any) => { inputRefs.current[`charge_${idx}_ledger`] = el; }}
                      type="text"
                      value={focusedField === `charge_${idx}_ledger` ? charge.ledger.text : charge.ledger.value}
                      onChange={e => handleInputChange(`charge_${idx}_ledger`, e.target.value)}
                      onFocus={(e: any) => { e.stopPropagation(); focusField(`charge_${idx}_ledger`); }}
                      onKeyDown={e => handleKeyDown(e as any, `charge_${idx}_ledger`)}
                      className={`w-full max-w-full h-full box-border min-w-0 outline-none px-2 py-[2px] font-medium border-transparent text-[#1e8585] ${focusedField === `charge_${idx}_ledger` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                      spellCheck={false}
                    />
                  </div>
                  <div className="w-[80px] min-w-[80px] max-w-[80px] shrink-0 border-r border-[#f0f0f0] box-border" />
                  <div className="w-[100px] min-w-[100px] max-w-[100px] shrink-0 border-r border-[#f0f0f0] box-border" />
                  <div className="w-[65px] min-w-[65px] max-w-[65px] shrink-0 border-r border-[#f0f0f0] box-border" />
                  <div className={`w-[160px] min-w-[160px] max-w-[160px] shrink-0 box-border ${!active ? 'pointer-events-none' : ''}`}>
                    {active && (
                      <input
                        ref={(el: any) => { inputRefs.current[`charge_${idx}_amount`] = el; }}
                        type="text"
                        value={charge.amount}
                        onChange={e => handleInputChange(`charge_${idx}_amount`, e.target.value)}
                        onFocus={(e: any) => { e.stopPropagation(); focusField(`charge_${idx}_amount`); }}
                        onKeyDown={e => handleKeyDown(e as any, `charge_${idx}_amount`)}
                        className={`w-full max-w-full h-full box-border min-w-0 outline-none px-2 py-[2px] text-right font-semibold text-[#1e8585] ${focusedField === `charge_${idx}_amount` ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ======================= */}
      {/* FOOTER: TOTALS + NARRATION */}
      {/* ======================= */}
      <div className="flex flex-col shrink-0 flex-none border-t border-[#aebec8] bg-[#eef6ea] z-10 w-full mt-auto">
        {cfg?.useJournalGrid && (
          <div className="flex font-bold text-[13px] w-full box-border">
            <div className="flex-1 min-w-0 px-2 py-[4px] text-right border-r border-[#aebec8] box-border">Total</div>
            <div className="w-[130px] min-w-[130px] max-w-[130px] shrink-0 px-2 py-[4px] text-right border-r border-[#aebec8] box-border">{fmt(drTotal)}</div>
            <div className="w-[130px] min-w-[130px] max-w-[130px] shrink-0 px-2 py-[4px] text-right box-border">{fmt(crTotal)}</div>
          </div>
        )}
        {cfg?.useItemGrid && (
          <div className="flex font-bold text-[13px] w-full box-border">
            <div className="flex-1 min-w-0 px-2 py-[4px] text-right box-border">Total</div>
            <div className="w-[160px] min-w-[160px] max-w-[160px] shrink-0 px-2 py-[4px] text-right border-l border-[#aebec8] box-border">
              {baseCurrency?.symbol || ''} {fmt(grandTotal)}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
